// ============================================================================
// <ApprovalInbox> — pending approvals visible to the current user's roles.
// ============================================================================
// Approve / Reject / Cancel via a small inline dialog that captures a comment.

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import type { ApprovalInboxItem, WorkflowApprovalDecision } from '../api/types';
import { workflowService } from '../api/workflowService';
import { useApprovalInbox } from '../hooks/useApprovalInbox';

interface Props {
  onResolved?: () => void;
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

interface ResolveModalState {
  item: ApprovalInboxItem;
  decision: WorkflowApprovalDecision;
}

export const ApprovalInbox: React.FC<Props> = ({ onResolved }) => {
  const { data, loading, error, refetch } = useApprovalInbox();
  const [resolving, setResolving] = useState<ResolveModalState | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const open = useCallback((item: ApprovalInboxItem, decision: WorkflowApprovalDecision) => {
    setComment('');
    setResolving({ item, decision });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!resolving) return;
    setBusy(true);
    try {
      await workflowService.resolveApproval(resolving.item.approvalId, {
        decision: resolving.decision,
        comment: comment || undefined,
      });
      notificationService.toast.success(`Approval ${resolving.decision.toLowerCase()}d.`);
      setResolving(null);
      await refetch();
      onResolved?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Resolution failed.';
      notificationService.toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [resolving, comment, refetch, onResolved]);

  if (loading) return <div style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>Loading inbox…</div>;
  if (error) return <div style={{ padding: '0.75rem', color: '#dc2626' }}>Failed to load inbox.</div>;
  if (data.length === 0) {
    return <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No pending approvals.</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem' }}>
        {data.map((item) => (
          <div
            key={item.approvalId}
            style={{
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              borderRadius: '6px',
              padding: '0.75rem 1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <IconSystem.alert size={16} style={{ color: '#d97706' }} />
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                {item.definitionCode}
              </strong>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                · {item.entityType}
              </span>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Requested by <strong>{item.requestedByUserName ?? 'unknown'}</strong> · {formatDate(item.requestedAt)}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button className="btn btn-primary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
                onClick={() => open(item, 'Approve')}>Approve</button>
              <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
                onClick={() => open(item, 'Reject')}>Reject</button>
              <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
                onClick={() => open(item, 'Cancel')}>Cancel</button>
            </div>
          </div>
        ))}
      </div>

      {resolving && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{resolving.decision} approval</h2>
              <button className="modal-close" onClick={() => !busy && setResolving(null)} disabled={busy}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                {resolving.decision === 'Approve' && 'Approving this request will fire the deferred state transition.'}
                {resolving.decision === 'Reject' && 'Rejecting this request will close the approval without changing state.'}
                {resolving.decision === 'Cancel' && 'Cancelling will close the approval and leave the workflow as-is.'}
              </p>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                Comment {resolving.decision === 'Reject' ? <span className="text-danger">(recommended)</span> : '(optional)'}
              </label>
              <textarea
                className="form-textarea"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{ width: '100%' }}
                disabled={busy}
              />
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => !busy && setResolving(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirm} disabled={busy}>
                {busy ? 'Working…' : resolving.decision}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ApprovalInbox;
